import type {
  FormPackageV2SubmitPayload,
  PolicySlot,
  V2AgreementAnswer,
  V2AgreementSignatureFieldAnswer,
  V2FieldAnswer,
  V2FormAnswer,
  V2TemplateAgreementAnswer,
} from '../../api/bookingApi';
import { slotKeys, type SlotKey, type StagedUpload, type V2Item, type V2Package } from './types';
import { isFieldVisible } from './fieldVisibility';
import { isFieldRequired } from './fieldRequiredness';
import { POLICY_SLOTS } from './insuranceCheck';

// Deterministic assembly of the v2 submit document (backend#2049 contract).
// Same state in → byte-identical JSON out, so a retry under the same
// idempotency key replays instead of conflicting. Never null, never base64,
// never a timestamp; numbers travel as the strings the patient typed.

export interface V2AnswerState {
  agreedItemIds: string[];
  agreementFieldValues: Record<string, Record<number, string>>;
  formValues: Record<string, Record<string, unknown>>;
  uploads: Record<SlotKey, StagedUpload>;
  consentName: string;
  consentRequired: boolean;
  /** Each slot's submittable check_id, already resolved against the answers being
   *  submitted (`submittableInsuranceChecks`); a slot without one is left out. */
  insuranceChecks: Partial<Record<PolicySlot, string>>;
}

/** A required answer is missing at build time — defensive; validateStep should have caught it. */
export class V2AssemblyError extends Error {
  itemId: string;

  constructor(itemId: string, message: string) {
    super(message);
    this.name = 'V2AssemblyError';
    this.itemId = itemId;
    Object.setPrototypeOf(this, V2AssemblyError.prototype);
  }
}

function normalizeScalar(value: unknown): V2FieldAnswer | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value.trim() === '' ? undefined : value;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined;
  if (Array.isArray(value)) {
    const strings = value.filter((v): v is string => typeof v === 'string' && v.trim() !== '');
    return strings.length ? strings : undefined;
  }
  return undefined;
}

/** A payer ID as the backend accepts it beside a company name (backend#3196): a
 *  trimmed, nonblank string of at most 200 chars; anything else is no companion. */
export function payerCompanion(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed !== '' && trimmed.length <= 200 ? trimmed : undefined;
}

export function buildV2SubmitPayload(
  pkg: V2Package,
  idempotencyKey: string,
  state: V2AnswerState,
): FormPackageV2SubmitPayload {
  const answers: Record<string, V2AgreementAnswer | V2TemplateAgreementAnswer | V2FormAnswer> = {};
  const referenced: string[] = [];
  const seen: Record<string, true> = {};
  const reference = (uploadId: string) => {
    if (!seen[uploadId]) {
      seen[uploadId] = true;
      referenced.push(uploadId);
    }
  };

  // One item's template fields → answers. static_text carries no answer; signatures,
  // initials and files reference their staged upload; scalars are normalized.
  const collectFields = (item: V2Item): V2FormAnswer => {
    const id = item.instanceItemId;
    const sections = item.sections;
    const out: V2FormAnswer = {};
    const values = state.formValues[id] ?? {};
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.field_type === 'static_text') return;
        // A conditionally-hidden field is omitted entirely — never required, never
        // posted (matches what the patient saw; backend must not flag it missing).
        if (!isFieldVisible(field, values, sections)) return;
        if (field.field_type === 'signature' || field.field_type === 'initials') {
          const slot = state.uploads[slotKeys.fieldSig(id, field.key)];
          if (!slot) {
            if (field.required) throw new V2AssemblyError(id, `Please complete "${field.label}".`);
            return;
          }
          reference(slot.uploadId);
          out[field.key] = { upload_id: slot.uploadId };
          return;
        }
        if (field.field_type === 'file_upload') {
          const slot = state.uploads[slotKeys.fieldFile(id, field.key)];
          if (!slot) {
            if (field.required) throw new V2AssemblyError(id, `Please upload "${field.label}".`);
            return;
          }
          reference(slot.uploadId);
          out[field.key] = { upload_id: slot.uploadId };
          return;
        }
        if (field.field_type === 'checkbox' && !field.must_be_true && isFieldRequired(item, field, values)
            && typeof values[field.key] !== 'boolean') {
          throw new V2AssemblyError(id, `Please answer "${field.label}".`);
        }
        if (field.field_type === 'checkbox' && field.must_be_true && field.required && typeof values[field.key] !== 'boolean'
            && sections.some((s) => s.fields.some((f) => f.show_if?.field === field.key))) {
          throw new V2AssemblyError(id, `Please answer "${field.label}".`);
        }
        const normalized = normalizeScalar(values[field.key]);
        if (normalized !== undefined) {
          out[field.key] = normalized;
          // A picked payer's stedi_id rides along under the field's payer_id_key
          // (backend#3196), only beside a nonblank company name.
          const companionKey = typeof normalized === 'string' ? field.payer_id_key : null;
          const companion = companionKey ? payerCompanion(values[companionKey]) : undefined;
          if (companionKey && companion) out[companionKey] = companion;
        } else if (field.field_type === 'checkbox' && field.must_be_true && field.required) {
          // Preserve the legacy affirmation fallback; required Yes/No questions
          // must already have an explicit boolean above.
          out[field.key] = false;
        }
      });
    });
    return out;
  };

  const items = pkg.items.slice().sort((a, b) => a.ordinal - b.ordinal);
  items.forEach((item) => {
    const id = item.instanceItemId;
    if (item.kind === 'agreement') {
      const agreed = state.agreedItemIds.indexOf(id) !== -1;
      if (!agreed) {
        if (item.required) throw new V2AssemblyError(id, `Please agree to "${item.name}" to continue.`);
        return;
      }
      if (item.mode === 'template') {
        // Form-builder agreement: `agreed` plus its own fields, keyed like an intake item.
        answers[id] = { agreed: true, ...collectFields(item) };
        return;
      }
      // Index-aligned with the revision's signature_fields: an unanswered OPTIONAL
      // entry stays in place as `{label, type}` so later indexes never shift.
      const signatureFields: V2AgreementSignatureFieldAnswer[] = [];
      item.signatureFields.forEach((sf, index) => {
        if (sf.type === 'signature') {
          const slot = state.uploads[slotKeys.agreementSig(id, index)];
          if (!slot) {
            if (sf.required) throw new V2AssemblyError(id, `Please sign "${sf.label}".`);
            signatureFields.push({ label: sf.label, type: sf.type });
            return;
          }
          reference(slot.uploadId);
          signatureFields.push({ label: sf.label, type: 'signature', upload_id: slot.uploadId });
        } else {
          const value = state.agreementFieldValues[id]?.[index] ?? '';
          if (value.trim() === '') {
            if (sf.required) throw new V2AssemblyError(id, `Please fill in "${sf.label}".`);
            signatureFields.push({ label: sf.label, type: sf.type });
            return;
          }
          signatureFields.push({ label: sf.label, type: sf.type, value });
        }
      });
      answers[id] = { agreed: true, signature_fields: signatureFields };
      return;
    }

    const out = collectFields(item);
    if (item.required || Object.keys(out).length > 0) answers[id] = out;
  });

  // The final authorization is posted iff at least one agreement was accepted
  // (backend#1998 rejects a consent with nothing to authorize, and requires one
  // otherwise); a drawing left over from a since-declined agreement is dropped.
  const consentSlot = state.uploads[slotKeys.consent];
  const consentName = state.consentName.trim();
  if (state.consentRequired && (!consentSlot || !consentName)) {
    throw new V2AssemblyError('consent', 'Please complete the final authorization.');
  }
  const consent = state.consentRequired && consentSlot && consentName
    ? { name: consentName, signature_upload_id: consentSlot.uploadId }
    : undefined;
  if (consent) reference(consent.signature_upload_id);

  // A slot is either referenced or left out, never `{check_id: null}`, and the key
  // is absent when no slot is, so a submission without checks keeps its old shape
  // (and request hash). Slots go in a fixed order: primary, then secondary.
  const insuranceChecks: NonNullable<FormPackageV2SubmitPayload['insurance_checks']> = {};
  POLICY_SLOTS.forEach((slot) => {
    const checkId = state.insuranceChecks[slot];
    if (checkId) insuranceChecks[slot] = { check_id: checkId };
  });

  const payload: FormPackageV2SubmitPayload = { request_idempotency_key: idempotencyKey, answers, upload_ids: referenced };
  if (consent) payload.consent = consent;
  if (Object.keys(insuranceChecks).length > 0) payload.insurance_checks = insuranceChecks;
  return payload;
}
